import type { KeyboardEvent } from 'react';

import { View } from '../../primitives/View';
import { CHROME_GLASS_INTENSITY } from '../../styles/glass';
import { cn } from '../../utils/cn';
import { bottomSheetHandleClass } from '../BottomSheet/BottomSheet.variants';
import { OverlayGlass } from '../Glass';
import { OVERLAY_FILL_CLASS_ANDROID } from '../Tooltip/surface';
import type { DropdownMenuProps } from './DropdownMenu.types';
import { DROPDOWN_MENU_RADIUS_PX, dropdownMenuVariants } from './DropdownMenu.variants';
import { menuArrowKeyNav } from './menuKeyboardNav';

/**
 * Cross-platform DropdownMenu panel. Renders the rounded, shadowed surface
 * that holds a stack of `MenuListItem` rows (or any other content).
 *
 * Figma reference: file `YZK9r3wDhvw1pY60KlVEIj`, section `1130:17622`
 * ("Dropdown_menu"). `size` maps the row band the panel hosts onto the
 * panel's corner radius (56/48→24, 44→20, 32→18); `withHandle` adds the
 * bottom-sheet drag indicator ("Extended-mobile" frame).
 *
 * **Not a popover.** This component renders the *body* only. Pair it with
 * a popover library (Radix on web, the shared bottom sheet on mobile) when
 * the panel needs anchored positioning and dismiss-on-outside-click. For
 * an opinionated select-style flow with a Button trigger see the sibling
 * `Dropdown`.
 *
 * @example
 * ```tsx
 * import { DropdownMenu, MenuListItem } from '@flashka/shared/ui';
 * import { Edit2, Trash2 } from '@flashka/shared/ui/icons';
 *
 * <DropdownMenu size="xl" withHandle>
 *   <MenuListItem size="xl" leftIcon={Edit2} onPress={onEdit}>Edit</MenuListItem>
 *   <MenuListItem size="xl" leftIcon={Trash2} onPress={onDelete}>Delete</MenuListItem>
 * </DropdownMenu>
 * ```
 */
export function DropdownMenu({
  children,
  size,
  withHandle,
  className,
  testID,
  webProps,
  nativeProps,
}: DropdownMenuProps) {
  // Default to `xl` (radius 24 — Figma `1284:12478`), the slim floating
  // popover most callers want; pass a smaller `size` so the panel radius
  // tracks the row band it hosts (44→20, 32→18).
  const resolvedSize = size ?? 'xl';
  // `webProps` is the union of the button / anchor / div handler shapes, so a
  // composed handler needs one narrowing cast — same reason, same shape, as
  // `Select.tsx`'s `callerOnClick`.
  const callerOnKeyDown = webProps?.onKeyDown as
    | ((event: KeyboardEvent<HTMLElement>) => void)
    | undefined;

  return (
    <View
      className={cn(dropdownMenuVariants({ size: resolvedSize }), className)}
      testID={testID}
      accessibilityRole="menu"
      webProps={{
        ...webProps,
        // Up/Down walk the rows, wherever inside the panel focus currently is
        // (including a Select's search field). No-op on native.
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
          callerOnKeyDown?.(event);
          menuArrowKeyNav(event);
        },
      }}
      nativeProps={nativeProps}>
      {/* Frosted-glass backdrop. Web is a no-op (the panel's own
          `web:backdrop-blur-sm` renders the CSS glass); on iOS and ANDROID the
          DS `GlassBackground` drops a real blur behind the rows.

          ANDROID DOES FROST HERE, and that corrects a claim this file used to
          make. The reasoning was that an anchored menu presents inside a React
          Native `Modal`, which Android puts in its own window, so expo-blur
          would have nothing to sample. The window boundary turns out not to
          matter: the Dimezis backend blurs by DRAWING the nominated
          `GlassBlurTarget` view tree into its own canvas, and a view in
          another window is still an attached, drawable view. Verified on a
          Pixel 10 emulator (API 37): with `frostOnAndroid` on, the page behind
          the menu goes from 2.9% to 53.6% soft content — i.e. it blurs, the
          same way iOS does.

          `fallbackClassName` still matters: below SDK 31, or with no
          `GlassBlurTarget` mounted, there is no frost and the panel needs the
          opaque-enough fill that Tooltip and Popover share. */}
      <OverlayGlass
        intensity={CHROME_GLASS_INTENSITY}
        radius={DROPDOWN_MENU_RADIUS_PX[resolvedSize]}
        fallbackClassName={OVERLAY_FILL_CLASS_ANDROID}
      />
      {withHandle ? <DragHandle /> : null}
      {children}
    </View>
  );
}

/**
 * iOS-style drag handle indicator — a small horizontal pill at the top of
 * the panel. Rendered only when `withHandle` is set (typically for the
 * Figma "Extended-Mobile" variant where the menu sits inside a bottom
 * sheet that the user can swipe to dismiss).
 *
 * Pure decoration — does NOT wire any gesture. The wrapping bottom sheet
 * is responsible for the swipe-to-dismiss behaviour; this just gives the
 * sheet a visual affordance so the user knows it's draggable.
 */
function DragHandle() {
  return (
    <View
      // 4-px vertical padding around the handle row; the panel's own `gap-1`
      // then separates it from the first row (Figma "Extended-mobile"
      // `1284:12992`: handle wrapper `py-[4px]` + panel `gap-[4px]`). Hidden
      // from screen readers — the handle has no semantic value and the sheet
      // wrapper already announces itself.
      className="flex flex-row items-center justify-center py-1"
      webProps={{ 'aria-hidden': true }}>
      {/* The DS sheet handle (55 × 4). Figma's Extended-mobile node
          `1284:12994` draws a 96px pill, but every sheet-like surface shares
          ONE handle spec — the DS BottomSheet's — so the width can't drift
          per component. */}
      <View className={bottomSheetHandleClass} />
    </View>
  );
}
